// The 3D view's camera moves (docs/claude/rendering.md, *Navigating in 3D*): the
// standard views, a double-click's focus and the eased glide from one pose to
// another. DOM-free; poses in drawing coordinates (plan x, y and the height z).
import { type Pt3 } from './handles';

/** Where the camera stands and the point it orbits (and looks at). */
export interface CameraPose { eye: Pt3; target: Pt3 }

export type StandardView =
  | 'back'
  | 'front'
  | 'iso'
  | 'left'
  | 'right'
  | 'top';

/** Each standard view: its menu label and the side it looks from (from the
 *  target toward the eye). Top looks down with north up, as the plan does — a
 *  hair to the south, so the orbit keeps its heading; Front from the south,
 *  Back from the north, Left from the west, Right from the east; Isometric from
 *  the south-east, as high as it is far. Listed in the menu's order. */
export const STANDARD_VIEWS: Record<StandardView, { label: string; from: Pt3 }> = {
  top: { label: 'Top view', from: { x: 0, y: -1e-3, z: 1 } },
  front: { label: 'Front view', from: { x: 0, y: -1, z: 0 } },
  back: { label: 'Back view', from: { x: 0, y: 1, z: 0 } },
  left: { label: 'Left view', from: { x: -1, y: 0, z: 0 } },
  right: { label: 'Right view', from: { x: 1, y: 0, z: 0 } },
  iso: { label: 'Isometric view', from: { x: 1, y: -1, z: 1 } },
};

const sub = (a: Pt3, b: Pt3): Pt3 => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });
const add = (a: Pt3, b: Pt3): Pt3 => ({ x: a.x + b.x, y: a.y + b.y, z: a.z + b.z });
const scale = (a: Pt3, k: number): Pt3 => ({ x: a.x * k, y: a.y * k, z: a.z * k });
const length = (a: Pt3): number => Math.hypot(a.x, a.y, a.z);

/** The camera's distance from what it orbits. */
export const poseDistance = (p: CameraPose): number => length(sub(p.eye, p.target));

/** A standard view of the point `target`, from `dist` away. */
export function standardPose(view: StandardView, target: Pt3, dist: number): CameraPose {
  const f = STANDARD_VIEWS[view].from, l = length(f) || 1;
  return { eye: add(target, scale(f, dist / l)), target: { ...target } };
}

/** The camera moved so it orbits `p` (a double-click's point), seen as before:
 *  the same angle and distance — `p` comes to the middle of the view. */
export function focusPose(pose: CameraPose, p: Pt3): CameraPose {
  const d = sub(p, pose.target);
  return { eye: add(pose.eye, d), target: { ...p } };
}

/** The camera `factor` times as far from what it orbits (< 1 closer), along the
 *  line it looks down: the zoom buttons in 3D. */
export function dollyPose(pose: CameraPose, factor: number): CameraPose {
  return { eye: add(pose.target, scale(sub(pose.eye, pose.target), factor)), target: { ...pose.target } };
}

/** Slow out of the start and into the end (0 → 0, 1 → 1). */
export function easeInOut(t: number): number {
  const u = Math.min(1, Math.max(0, t));
  return u < 0.5 ? 4 * u * u * u : 1 - Math.pow(-2 * u + 2, 3) / 2;
}

/** The pose `t` of the way (0–1, eased) from `a` to `b`: the target slides, and
 *  the eye swings round it — its heading the shorter way, its elevation and its
 *  distance in step — so a turn from Front to Back circles the model instead of
 *  passing through it. */
export function poseAt(a: CameraPose, b: CameraPose, t: number): CameraPose {
  const k = easeInOut(t);
  if (k <= 0) return { eye: { ...a.eye }, target: { ...a.target } };
  const target = add(a.target, scale(sub(b.target, a.target), k));
  const polar = (o: Pt3): { r: number; az: number; el: number } => {
    const r = length(o);
    return { r, az: Math.atan2(o.y, o.x), el: r > 0 ? Math.asin(Math.max(-1, Math.min(1, o.z / r))) : 0 };
  };
  const p = polar(sub(a.eye, a.target)), q = polar(sub(b.eye, b.target));
  let dAz = q.az - p.az;
  while (dAz > Math.PI) dAz -= 2 * Math.PI;
  while (dAz < -Math.PI) dAz += 2 * Math.PI;
  const r = p.r + (q.r - p.r) * k, az = p.az + dAz * k, el = p.el + (q.el - p.el) * k;
  const eye = add(target, { x: r * Math.cos(el) * Math.cos(az), y: r * Math.cos(el) * Math.sin(az), z: r * Math.sin(el) });
  return k >= 1 ? { eye: { ...b.eye }, target: { ...b.target } } : { eye, target };
}

/** The steepest the view may look up or down while orbiting (radians, 89°). */
const MAX_PITCH = (89 * Math.PI) / 180;

/** The camera turned about `pivot` (the selection's middle) — `dAz` about the
 *  vertical, then `dEl` about the level line across the view, lifting the eye
 *  as the view tips down, as the orbit's own drag does — eye and target
 *  together, so the view turns with no jump and the pivot stays where it shows.
 *  An elevation that would tip the view past 89° up or down is left out, unless
 *  it brings it back toward level (out of the Top view). */
export function orbitAbout(pose: CameraPose, pivot: Pt3, dAz: number, dEl: number): CameraPose {
  const turnZ = (p: Pt3, a: number): Pt3 => {
    const v = sub(p, pivot), c = Math.cos(a), s = Math.sin(a);
    return add(pivot, { x: v.x * c - v.y * s, y: v.x * s + v.y * c, z: v.z });
  };
  let eye = turnZ(pose.eye, dAz), target = turnZ(pose.target, dAz);
  const look = sub(target, eye), ll = length(look), lh = Math.hypot(look.x, look.y);
  if (lh > 1e-9 && ll > 0 && dEl) {
    const pitch = Math.asin(Math.max(-1, Math.min(1, look.z / ll))), next = pitch - dEl;
    if (Math.abs(next) < MAX_PITCH || Math.abs(next) < Math.abs(pitch)) {
      // About the level line across the view (Rodrigues), through the pivot.
      const k = { x: -look.y / lh, y: look.x / lh, z: 0 };
      const c = Math.cos(dEl), sn = Math.sin(dEl);
      const turn = (p: Pt3): Pt3 => {
        const v = sub(p, pivot);
        const kv = { x: k.y * v.z - k.z * v.y, y: k.z * v.x - k.x * v.z, z: k.x * v.y - k.y * v.x };
        const kd = k.x * v.x + k.y * v.y + k.z * v.z;
        return add(pivot, { x: v.x * c + kv.x * sn + k.x * kd * (1 - c), y: v.y * c + kv.y * sn + k.y * kd * (1 - c), z: v.z * c + kv.z * sn + k.z * kd * (1 - c) });
      };
      eye = turn(eye);
      target = turn(target);
    }
  }
  return { eye, target };
}
